import React, { useState, useEffect, useMemo, useRef } from "react";
import { ResponsivePie } from "@nivo/pie";
import PropTypes from "prop-types";
import { useSearchParams, useLocation } from "react-router-dom";
import { Empty } from "antd";
import { 
  fetchInviteClaraResponseCategorization, 
  fetchWtwClaraResponseCategorization,
  fetchClaraResponseCategorizationForIframe
} from "../services/api";
import { 
  ENV_TYPE_PROD,
  CATEGORY_TYPE_CLARA_RESPONSE,
  CATEGORY_TYPE_USER_QUERY
} from "../constants";

/**
 * Custom hook to measure element size using ResizeObserver
 * @returns {[React.Ref, {width: number, height: number}]} A ref to attach to an element and its current size
 */
const useElementSize = () => {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Check if ResizeObserver is available
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      if (entries.length === 0) return;
      const { width, height } = entries[0].contentRect;
      
      // Round to avoid unnecessary updates due to floating point differences
      const roundedWidth = Math.round(width);
      const roundedHeight = Math.round(height);
      
      setSize((prevSize) => {
        if (prevSize.width === roundedWidth && prevSize.height === roundedHeight) {
          return prevSize;
        }
        return { width: roundedWidth, height: roundedHeight };
      });
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, size];
};

/**
 * Intelligently shorten category labels for mobile display
 * @param {string} label - Full category label
 * @returns {string} Shortened label
 */
const shortenLabel = (label) => {
  const shortMappings = {
    'Clarification & Understanding': 'Clarification',
    'Reflection & Introspection': 'Reflection',
    'Social Conversations': 'Social',
    'Imagery & Detail': 'Imagery',
    'Research & Factual Information': 'Research',
    'Grammar, Mechanics, & Formatting': 'Grammar',
    'Characterization & Relationships': 'Characters',
    'Brainstorming & Inspiration': 'Brainstorming',
    'Writing Structure & Organization': 'Structure',
    'Revision & Editing': 'Revision',
    'Plot Development & Pacing': 'Plot',
    'Dialogue & Voice': 'Dialogue',
    'World Building': 'World Building',
    'Theme & Symbolism': 'Theme',
    'Creative Exercises': 'Exercises',
    'Technical Writing': 'Technical',
    'Poetry & Verse': 'Poetry',
    'Genre-Specific': 'Genre',
    'Publishing & Career': 'Publishing',
    'Other': 'Other'
  };
  
  return shortMappings[label] || (label.length > 15 ? label.substring(0, 15) + '...' : label);
};

const ClaraFeedbackBreakdown = ({ id: propId, source: propSource, envType: propEnvType }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  // Check if this is an iframe route (no login required)
  const isIframeRoute = location.pathname.includes('/iframe/');
  
  // Get parameters from URL params, fallback to props (no default id - empty means all)
  const id = searchParams.get('id') || propId || null;
  const source = searchParams.get('source') || propSource || "invite-clara";
  const darkMode = searchParams.get('darkMode') === 'true';
  
  const envType = propEnvType ?? ENV_TYPE_PROD;
  
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryType, setCategoryType] = useState(CATEGORY_TYPE_USER_QUERY);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Measure the component's container for responsive behavior
  const [containerRef, containerSize] = useElementSize();

  // Fallback to aspect ratio for ultra-wide detection (original behavior)
  const isUltraWideAspect = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth / window.innerHeight > 2.2;
  }, []);

  // Layout decisions based on measured container width
  // Use window width as fallback if container width is 0
  const effectiveWidth = containerSize.width > 0 ? containerSize.width : window.innerWidth;
  
  const isMobile = useMemo(
    () => effectiveWidth < 640,
    [effectiveWidth]
  );

  const isCompact = useMemo(
    () => effectiveWidth < 900,
    [effectiveWidth]
  );

  const isWide = useMemo(
    () => (effectiveWidth >= 1200) || isUltraWideAspect,
    [effectiveWidth, isUltraWideAspect]
  );

  // Memoized sorted data for mobile bar chart
  const sortedData = useMemo(
    () => [...data].sort((a, b) => b.value - a.value),
    [data]
  );

  // Memoized maximum value for bar chart percentage calculations
  const maxValue = useMemo(
    () => Math.max(0, ...sortedData.map((item) => item.value)),
    [sortedData]
  );

  // Memoized total count summary
  const totalCount = useMemo(
    () => sortedData.reduce((sum, item) => sum + item.value, 0),
    [sortedData]
  );

  // Memoized color mapping from category ID to stable color using golden ratio
  const colorById = useMemo(
    () => {
      if (data.length === 0) return {};
      
      const goldenRatioConjugate = 0.618033988749895;
      
      // Create deterministic starting hue from hash of all data
      let seedHash = 0;
      const seedStr = data.map((d) => d.id).join('|');
      for (let i = 0; i < seedStr.length; i++) {
        seedHash = seedStr.charCodeAt(i) + ((seedHash << 5) - seedHash);
      }
      
      let huePosition = (Math.abs(seedHash) % 1000000) / 1000000;
      
      return Object.fromEntries(
        data.map((item, index) => {
          // Iterate hue using golden ratio for optimal spacing
          huePosition += goldenRatioConjugate;
          huePosition %= 1;
          
          const hue = Math.floor(huePosition * 360);
          const saturation = 65 + (index % 3) * 10; // 65%, 75%, 85%
          const lightness = 50 + (Math.floor(index / 3) % 4) * 5; // 50%, 55%, 60%, 65%
          
          return [String(item.id), `hsl(${hue}, ${saturation}%, ${lightness}%)`];
        })
      );
    },
    [data]
  );

  // Memoized responsive Nivo chart configuration
  const chartLayout = useMemo(() => {
    if (effectiveWidth < 900) {
      return {
        margin: { top: 30, right: 70, bottom: 30, left: 70 },
        fontSize: 11,
        enableArcLabels: false,
        diagonalLength: 8,
        straightLength: 10
      };
    } else if (effectiveWidth < 1200) {
      return {
        margin: { top: 40, right: 120, bottom: 40, left: 120 },
        fontSize: 12,
        enableArcLabels: true,
        diagonalLength: 12,
        straightLength: 16
      };
    } else {
      return {
        margin: { top: 50, right: 175, bottom: 50, left: 175 },
        fontSize: 14,
        enableArcLabels: true,
        diagonalLength: 16,
        straightLength: 24
      };
    }
  }, [effectiveWidth]);

  useEffect(() => {
    const fetchData = async () => {
      if (!source) {
        setError("Missing required parameter: source is required");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        let response;
        
        // Use iframe endpoint for iframe routes (origin-validated, no login)
        if (isIframeRoute) {
          response = await fetchClaraResponseCategorizationForIframe(id, source, envType, categoryType);
        } 
        // Use authenticated endpoints for normal routes (requires login)
        else {
          if (source === "invite-clara") {
            response = await fetchInviteClaraResponseCategorization(id);
          } else if (source === "wtw-clara") {
            response = await fetchWtwClaraResponseCategorization(id);
          } else {
            throw new Error(`Invalid source: ${source}. Must be 'invite-clara' or 'wtw-clara'`);
          }
        }

        if (response.success) {
          // Determine which field to use based on category type
          const categoryField = categoryType === CATEGORY_TYPE_USER_QUERY 
            ? 'user_query_category' 
            : 'clara_response_category';
          
          // Transform API response to chart data format
          const processedData = response.data.map((item) => ({
            id: item[categoryField],
            value: item.count,
            label: item[categoryField]
          }));
          setData(processedData);
        } else {
          throw new Error(response.error || "Failed to fetch data");
        }
      } catch (err) {
        console.error("Error fetching Clara data:", err);
        
       
        if (err.response?.status === 403) {
          setError("Access forbidden. This iframe can only be embedded on authorized domains.");
        } else {
          setError(err.message || "Failed to fetch data");
        }
      } finally {
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    };

    fetchData();
  }, [id, source, envType, isIframeRoute, categoryType]);

  // Show initial loading screen only on first load
  if (isInitialLoad && isLoading) {
    return (
      <div className={`fixed inset-0 flex items-center justify-center min-h-screen w-full z-50 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <span className={`text-xl font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Checking Authentication...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`fixed inset-0 flex items-center justify-center min-h-screen w-full z-50 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className={`rounded-lg shadow-lg p-8 text-center ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h1 className={`text-3xl font-bold mb-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>Error</h1>
          <p className={`text-lg mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Access Denied</p>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            You do not have the required permissions to view this content.
          </p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={`flex min-h-screen w-full items-center justify-center p-2 sm:p-4 lg:p-6 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <div
          className={`flex min-h-[420px] w-full max-w-screen-2xl flex-col items-center justify-center rounded-lg p-3 sm:p-5 lg:p-6 text-center ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span className={darkMode ? 'text-gray-300' : 'text-gray-500'}>No data</span>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-2 sm:p-4 lg:p-6 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
      <div className="mx-auto w-full max-w-screen-2xl">
        <div
          ref={containerRef}
          className={`rounded-lg p-3 sm:p-5 lg:p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
        >
          <div
            className={
              isWide
                ? "flex items-center gap-6"
                : "grid grid-cols-1 gap-4"
            }
          >
            {/* Header and Toggle Section */}
            {isWide ? (
              <div className="flex-shrink-0 flex items-center justify-center" style={{ width: '120px', height: '400px' }}>
                <div style={{ 
                  transform: 'rotate(-90deg)', 
                  transformOrigin: 'center center',
                  width: '600px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <h1 className={`text-3xl font-bold mb-4 text-center whitespace-nowrap ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    Clara Category Breakdown
                  </h1>
                  
                  {/* Category Type Toggle - Only show for iframe routes */}
                  {isIframeRoute && (
                    <div className="flex justify-center mb-6">
                      <div className="inline-flex rounded-lg border p-1" style={{
                        backgroundColor: darkMode ? '#374151' : '#ffffff',
                        borderColor: darkMode ? '#4b5563' : '#d1d5db'
                      }}>
                        <button
                          type="button"
                          onClick={() => setCategoryType(CATEGORY_TYPE_USER_QUERY)}
                          disabled={isLoading}
                          className={`rounded-md px-6 py-2 text-sm font-medium transition-all whitespace-nowrap ${
                            categoryType === CATEGORY_TYPE_USER_QUERY
                              ? "text-white shadow-sm"
                              : darkMode
                                ? "text-gray-300 hover:bg-gray-600"
                                : "text-gray-700 hover:bg-gray-100"
                          } ${isLoading ? "cursor-not-allowed opacity-50" : ""}`}
                          style={categoryType === CATEGORY_TYPE_USER_QUERY ? { backgroundColor: "#2f6f57" } : {}}
                        >
                          User Query Category
                        </button>
                        <button
                          type="button"
                          onClick={() => setCategoryType(CATEGORY_TYPE_CLARA_RESPONSE)}
                          disabled={isLoading}
                          className={`rounded-md px-6 py-2 text-sm font-medium transition-all whitespace-nowrap ${
                            categoryType === CATEGORY_TYPE_CLARA_RESPONSE
                              ? "text-white shadow-sm"
                              : darkMode
                                ? "text-gray-300 hover:bg-gray-600"
                                : "text-gray-700 hover:bg-gray-100"
                          } ${isLoading ? "cursor-not-allowed opacity-50" : ""}`}
                          style={categoryType === CATEGORY_TYPE_CLARA_RESPONSE ? { backgroundColor: "#2f6f57" } : {}}
                        >
                          Clara Response Category
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <header className="text-center">
                <h1 className={`text-3xl font-bold mb-4 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                  Clara Category Breakdown
                </h1>
                
                {/* Category Type Toggle - Only show for iframe routes */}
                {isIframeRoute && (
                  <div className="flex justify-center">
                    <div className={`grid w-full max-w-xl grid-cols-1 gap-1 rounded-lg border p-1 sm:grid-cols-2 ${
                      darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'
                    }`}>
                      <button
                        type="button"
                        onClick={() => setCategoryType(CATEGORY_TYPE_USER_QUERY)}
                        disabled={isLoading}
                        className={`rounded-md px-3 py-2 text-xs font-medium transition-all sm:px-4 sm:text-sm ${
                          categoryType === CATEGORY_TYPE_USER_QUERY
                            ? "text-white shadow-sm"
                            : darkMode
                              ? "text-gray-300 hover:bg-gray-600"
                              : "text-gray-700 hover:bg-gray-100"
                        } ${isLoading ? "cursor-not-allowed opacity-50" : ""}`}
                        style={categoryType === CATEGORY_TYPE_USER_QUERY ? { backgroundColor: "#2f6f57" } : {}}
                      >
                        User Query Category
                      </button>
                      <button
                        type="button"
                        onClick={() => setCategoryType(CATEGORY_TYPE_CLARA_RESPONSE)}
                        disabled={isLoading}
                        className={`rounded-md px-3 py-2 text-xs font-medium transition-all sm:px-4 sm:text-sm ${
                          categoryType === CATEGORY_TYPE_CLARA_RESPONSE
                            ? "text-white shadow-sm"
                            : darkMode
                              ? "text-gray-300 hover:bg-gray-600"
                              : "text-gray-700 hover:bg-gray-100"
                        } ${isLoading ? "cursor-not-allowed opacity-50" : ""}`}
                        style={categoryType === CATEGORY_TYPE_CLARA_RESPONSE ? { backgroundColor: "#2f6f57" } : {}}
                      >
                        Clara Response Category
                      </button>
                    </div>
                  </div>
                )}
              </header>
            )}
            
            {/* Chart Section */}
            <section className={`relative min-w-0 ${isWide ? 'flex-1' : ''}`}>
              {/* Loading overlay - only show during category toggle */}
              {!isInitialLoad && isLoading && (
                <div className={`absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-opacity-75 ${
                  darkMode ? 'bg-gray-800' : 'bg-white'
                }`}>
                  <div className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Updating chart...</div>
                </div>
              )}
              
              {/* Render custom HTML bar chart on mobile, pie chart on desktop */}
              {isMobile ? (
                <div className="w-full px-2 py-4">
                  {/* Legend/Title */}
                  <div className={`mb-6 text-center text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Tap any bar to see full category name
                  </div>
                  
                  {/* Custom HTML Bar Chart */}
                  <div className="space-y-3">
                    {sortedData.map((item, index) => {
                      const percentage =
                        maxValue > 0 ? (item.value / maxValue) * 100 : 0;
                      const shortLabel = shortenLabel(item.label);
                      
                      return (
                        <div 
                          key={item.id}
                          className={`group relative rounded-lg p-2 transition-all ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                          title={item.label}
                        >
                          {/* Label and Value Row */}
                          <div className="mb-1.5 flex items-center justify-between">
                            <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                              {shortLabel}
                            </span>
                            <span className={`ml-2 text-sm font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                              {item.value}
                            </span>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className={`h-8 w-full overflow-hidden rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-200'
                          }`}>
                            <div
                              className="flex items-center justify-end px-3 transition-all duration-500 ease-out"
                              style={{
                                width: `${Math.max(percentage, 5)}%`,
                                backgroundColor: colorById[String(item.id)]
                              }}
                            >
                              <span className="text-xs font-semibold text-white drop-shadow-md">
                                {item.value}
                              </span>
                            </div>
                          </div>
                          
                          {/* Hover/Touch Tooltip - Shows full label */}
                          {shortLabel !== item.label && (
                            <div className={`
                              pointer-events-none absolute left-0 right-0 top-full z-10 mt-1 border
                              rounded-lg p-3 shadow-2xl transition-opacity duration-200
                              opacity-0 group-active:opacity-100 group-hover:opacity-100
                              ${darkMode 
                                ? 'border-gray-600 bg-gray-800 text-gray-100' 
                                : 'border-gray-300 bg-white text-gray-800'
                              }
                            `}>
                              <div className="text-sm font-semibold">{item.label}</div>
                              <div className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                Count: {item.value}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Total Count Summary */}
                  <div className={`mt-6 border-t pt-4 text-center ${
                    darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'
                  }`}>
                    <span className="text-sm font-medium">Total Count: </span>
                    <span className="text-lg font-bold">
                      {totalCount}
                    </span>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    height: "clamp(420px, 65vh, 650px)",
                    width: "100%",
                    minWidth: 0
                  }}
                >
                  <ResponsivePie
                    data={data}
                    margin={chartLayout.margin}
                    innerRadius={0.4}
                    padAngle={2}
                    cornerRadius={4}
                    colors={({ id }) => colorById[String(id)] ?? "#3b82f6"}
                    borderWidth={1}
                    borderColor={darkMode ? "rgba(31, 41, 55, 0.8)" : "rgba(255, 255, 255, 0.8)"}
                    arcLabelsSkipAngle={10}
                    arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
                    
                    arcLinkLabelsSkipAngle={10}
                    arcLinkLabelsTextColor={darkMode ? "#e5e7eb" : "#333333"}
                    arcLinkLabelsThickness={2}
                    arcLinkLabelsColor={{ from: "color" }}
                    arcLinkLabelsDiagonalLength={chartLayout.diagonalLength}
                    arcLinkLabelsStraightLength={chartLayout.straightLength}
                    arcLinkLabelsOffset={0}
                    enableArcLabels={chartLayout.enableArcLabels}
                    arcLabelsRadiusOffset={0.6}
                    theme={{
                      labels: {
                        text: {
                          fontSize: chartLayout.fontSize,
                          fontWeight: 600,
                          fill: darkMode ? "#e5e7eb" : "#333333"
                        }
                      },
                      tooltip: {
                        container: {
                          background: darkMode ? "#1f2937" : "#ffffff",
                          color: darkMode ? "#e5e7eb" : "#333333"
                        }
                      }
                    }}
                    tooltip={({ datum }) => (
                      <div className={`rounded-xl border p-4 shadow-xl backdrop-blur-sm ${
                        darkMode 
                          ? 'border-gray-600 bg-gray-800' 
                          : 'border-gray-200 bg-white'
                      }`}>
                        <div className={`mb-1 text-lg font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{datum.label}</div>
                        <div className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Count: {datum.value}</div>
                      </div>
                    )}
                    animate={true}
                    motionConfig="gentle"
                  />
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

ClaraFeedbackBreakdown.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  source: PropTypes.oneOf(["invite-clara", "wtw-clara"]),
  envType: PropTypes.oneOf([0, 1, 2]), // 0 = test, 1 = production, 2 = dev
};

export default ClaraFeedbackBreakdown;
